import { Link } from 'react-router-dom'
import { cn } from '@/shared/ui/primitives'
import { Icon, type IconName } from '@/app/icons'

export interface TileSpec {
  to: string
  label: string
  value: number
  icon: IconName
  /** accent: the one number that is somebody's turn; danger: needs fixing. */
  tone?: 'accent' | 'danger'
  /** One line of context, only when there is a true one to say. */
  micro?: { text: string; icon: IconName; warn?: boolean }
}

/**
 * One count, and the list it counts.
 *
 * Zero is written as "Tidak ada": a lone 0 on a tile reads for a moment like
 * the screen failed to load. The colour only appears when the number does --
 * a red tile saying nothing needs fixing is a false alarm every morning.
 */
export function Tiles({ tiles }: { tiles: TileSpec[] }) {
  // auto-fit, not a fixed four: someone without approval rights sees three
  // tiles, and three in a four-column grid leave a gap that looks broken.
  return (
    <div className="relative z-10 -mt-14 grid gap-4 grid-cols-[repeat(auto-fit,minmax(13.5rem,1fr))]">
      {tiles.map((tile, index) => (
        <Tile key={tile.to} tile={tile} delay={index * 50 + 100} />
      ))}
    </div>
  )
}

function Tile({ tile, delay }: { tile: TileSpec; delay: number }) {
  const active = tile.value > 0
  const accent = active && tile.tone === 'accent'
  const danger = active && tile.tone === 'danger'

  return (
    <Link
      to={tile.to}
      style={{ animationDelay: `${delay}ms` }}
      className={cn(
        'group animate-rise flex flex-col gap-3.5 rounded-panel border p-4.5 transition hover:-translate-y-0.5',
        'shadow-panel hover:shadow-card-lg',
        accent
          ? 'border-transparent bg-linear-to-br from-[#0aa6ff] via-[#0072d6] to-[#0b3f9e] text-white hover:text-white'
          : 'border-line bg-card text-gray-700 hover:border-primary/50 hover:text-gray-700',
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'grid size-9.5 place-items-center rounded-lg',
            accent ? 'bg-white/20 text-white' : danger ? 'bg-danger-light text-danger' : 'bg-primary-light text-primary',
          )}
        >
          <Icon name={tile.icon} className="size-4.5" />
        </span>
        <Icon
          name="arrowRight"
          className={cn(
            'size-4 transition group-hover:translate-x-0.5',
            accent ? 'text-white/70' : 'text-gray-400 group-hover:text-primary',
          )}
        />
      </div>

      <div>
        {tile.value === 0 ? (
          <div className="text-lg leading-[2.1rem] font-semibold text-gray-400">Tidak ada</div>
        ) : (
          <div
            className={cn(
              'text-[2.1rem] leading-none font-extrabold tracking-tight tabular-nums',
              accent ? 'text-white' : danger ? 'text-danger' : 'text-gray-900',
            )}
          >
            {tile.value}
          </div>
        )}
        <div className={cn('mt-1 font-medium', accent ? 'text-white' : 'text-gray-700')}>{tile.label}</div>
      </div>

      <div
        className={cn(
          'flex items-center gap-1.5 border-t border-dashed pt-3 text-xs',
          accent ? 'border-white/25 text-white/85' : 'border-line text-gray-500',
          tile.micro?.warn && (accent ? 'text-[#ffe27a]' : 'text-warning-ink'),
        )}
      >
        {tile.value === 0 ? (
          <>
            <Icon name="check" className="size-3.5" /> Bersih
          </>
        ) : tile.micro ? (
          <>
            <Icon name={tile.micro.icon} className="size-3.5" /> {tile.micro.text}
          </>
        ) : (
          <>
            <Icon name="arrowRight" className="size-3.5" /> Buka daftarnya
          </>
        )}
      </div>
    </Link>
  )
}
